import React, {ComponentProps, forwardRef, ReactNode, Ref, useCallback, useEffect, useMemo, useState} from 'react';
import ReactMapGL, {
  MapRef,
  ViewStateChangeEvent,
  Source,
  Layer
} from 'react-map-gl/maplibre';
import maplibregl, {
  RequestTransformFunction,
  StyleSpecification
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {Viewport} from '@geomatico/geocomponents/types/common';

const cssStyle = {
  width: '100%',
  height: '100%'
};

type ReactMapGlProps = ComponentProps<typeof ReactMapGL>;

interface MapProps extends ReactMapGlProps {
  mapStyle?: string | StyleSpecification,
  maplibreLogo?: boolean
  auth?: Array<{
    urlMatch: string,
    user: string,
    password: string
  }>,
  sprite?: StyleSpecification['sprite'],
  glyphs?: StyleSpecification['glyphs'],
  terrain?: StyleSpecification['terrain'],
  sources?: StyleSpecification['sources'],
  layers?: StyleSpecification['layers'],
  firstTopLayer?: string,
  viewport?: Viewport,
  onViewportChange?: (viewport: Viewport) => void,
  children?: ReactNode
}

// TODO: Este componente trae los cambios propuestos en https://github.com/geomatico/geocomponents/pull/117
//       Cuando se haga merge, y se saque versión de geocomponents, se puede eliminar este componente
const Map = React.memo(forwardRef((props: MapProps, ref: Ref<MapRef>) => {
  const {
    mapStyle = 'https://tileserver.geomatico.es/styles/klokantech-basic/style.json',
    mapLib = maplibregl,
    auth,
    sprite,
    glyphs,
    terrain,
    sources = {},
    layers = [],
    firstTopLayer,
    maxZoom = 20,
    viewport = {
      latitude: 40.421860,
      longitude: -3.746338,
      zoom: 6,
      bearing: 0,
      pitch: 0
    },
    onViewportChange,
    children,
    ...reactMapGlProps
  } = props;

  const [baseStyle, setBaseStyle] = useState<StyleSpecification>();

  useEffect(() => {
    if (typeof mapStyle === 'string') {
      // a) A standard URL
      fetch(mapStyle).then(response => response.json()).then(setBaseStyle);
    } else {
      // b) An object literal
      setBaseStyle(mapStyle);
    }
  }, [mapStyle]);

  const fullStyle: StyleSpecification | undefined = useMemo(() => {
    if (!baseStyle) return;

    return {
      ...baseStyle,
      ...(sprite ? {sprite} : {}),
      ...(glyphs ? {glyphs} : {}),
      ...(terrain ? {terrain} : {}),
    };
  }, [baseStyle, sprite, glyphs, terrain]);

  const Sources = useMemo(() => {
    return Object.entries(sources).map(([id, source]) => (<Source key={id} id={id} {...source} />));
  }, [sources]);

  const Layers = useMemo(() => {
    return layers.map(layer => (<Layer key={layer.id} {...layer} beforeId={firstTopLayer} />));
  }, [layers, firstTopLayer]);

  const setBasicAuthHeader = useCallback<RequestTransformFunction>((url, resourceType) => {
    if (resourceType === 'Tile') {
      const match: { user: string, password: string } | undefined = auth?.find((rule: {
        urlMatch: string,
        user: string,
        password: string
      }) => url.match(rule.urlMatch));
      if (match) {
        return {
          url,
          headers: {'Authorization': 'Basic ' + btoa(match.user + ':' + match.password)}
        };
      } else {
        return {url};
      }
    } else {
      return {url};
    }
  }, [auth]);

  const transformRequest = useMemo(() =>
    auth ? setBasicAuthHeader : undefined,
  [setBasicAuthHeader]);

  const handleMapMove = useCallback(({viewState}: ViewStateChangeEvent) => {
    if (onViewportChange !== undefined) {
      const {latitude, longitude, zoom, bearing, pitch} = viewState;
      onViewportChange({latitude, longitude, zoom, bearing, pitch});
    }
  }, [onViewportChange]);

  return fullStyle ? <ReactMapGL
    ref={ref}
    {...reactMapGlProps}
    mapLib={mapLib}
    mapStyle={fullStyle}
    {...viewport}
    onMove={handleMapMove}
    transformRequest={transformRequest}
    style={cssStyle}
    maxZoom={maxZoom}
  >
    {Sources}
    {Layers}
    {children}
  </ReactMapGL> : null;
}));

Map.displayName = 'Map';

export default Map;